import { useEffect, useMemo } from "react";
import { Box, Stack, TextField } from "@mui/material";
import {
  useCoverageNoteDrafts,
  useUpsertCoverageNote,
  useDeleteCoverageNote,
} from "@platform/hooks";
import { useApi } from "@platform/api";
import {
  buildCoverageNoteSections,
  useCoverageOverview,
} from "@platform/coverage-overview";
import { useQueryClient } from "@tanstack/react-query";
import { AnchorNav, Button, Typography } from "@platform/design-system";

import { useAutosaveFlush } from "../../hooks/useAutosaveFlush";
import useToastNotificationService from "../../hooks/useToastNotificationService";

const MAX_LENGTH = 1024;

interface CoverageNoteContentProps {
  ecoRfpId: string;
  registerFlush?: (flush: () => void) => void;
}

const CoverageNoteContent = ({
  ecoRfpId,
  registerFlush,
}: CoverageNoteContentProps): React.JSX.Element => {
  const queryClient = useQueryClient();
  const axiosInstance = useApi();
  const { createToastNotification } = useToastNotificationService();

  const { coverageQuery } = useCoverageOverview({
    ecoRfpId,
    axiosInstance,
  });

  const coverages = useMemo(
    () => buildCoverageNoteSections(coverageQuery.data),
    [coverageQuery.data],
  );

  // Invalidate on mount so a page refresh always fetches fresh notes
  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ["send-rfp", ecoRfpId, "coverage-notes"],
    });
  }, [queryClient, ecoRfpId]);

  const {
    notes: localNotes,
    baselineNotes: serverNotes,
    isLoading,
    isError,
    setCoverageNote,
  } = useCoverageNoteDrafts({
    ecoRfpId,
    coverages: coverages.map((coverage) => ({
      id: coverage.coverageId,
      benefitPlanType: coverage.benefitPlanType,
      noteKey: coverage.noteKey,
    })),
    enabled: coverages.length > 0,
  });

  const upsertMutation = useUpsertCoverageNote({ ecoRfpId });
  const deleteMutation = useDeleteCoverageNote({ ecoRfpId });

  const handleNoteChange = (noteKey: string, value: string): void => {
    if (value.length <= MAX_LENGTH) {
      setCoverageNote(noteKey, value);
    }
  };

  const handleSave = (noteKey: string): void => {
    const content = (localNotes[noteKey] ?? "").trim();
    const coverage = coverages.find((item) => item.noteKey === noteKey);
    if (!coverage) {
      return;
    }

    if (content === "") {
      deleteMutation.mutate({
        coverageId: coverage.coverageId,
        benefitPlanType: coverage.benefitPlanType,
      });
    } else {
      upsertMutation.mutate({
        coverageId: coverage.coverageId,
        content,
        benefitPlanType: coverage.benefitPlanType,
      });
    }
  };

  const isSaving = upsertMutation.isPending || deleteMutation.isPending;

  // Save every dirty coverage draft.
  useAutosaveFlush(() => {
    const onError = (label: string): void => {
      createToastNotification({
        severity: "error",
        title: "Saving failed",
        message: `Your ${label} note wasn't saved. Please try again.`,
      });
    };
    coverages.forEach((coverage) => {
      const content = (localNotes[coverage.noteKey] ?? "").trim();
      const serverValue = (serverNotes?.[coverage.noteKey] ?? "").trim();
      if (content === serverValue) return;
      if (content === "") {
        deleteMutation.mutate(
          {
            coverageId: coverage.coverageId,
            benefitPlanType: coverage.benefitPlanType,
          },
          { onError: () => onError(coverage.label) },
        );
      } else {
        upsertMutation.mutate(
          {
            coverageId: coverage.coverageId,
            content,
            benefitPlanType: coverage.benefitPlanType,
          },
          { onError: () => onError(coverage.label) },
        );
      }
    });
  }, registerFlush);

  const anchorItems = useMemo(
    () =>
      coverages.map((cov) => ({
        id: cov.noteKey,
        label: cov.label,
      })),
    [coverages],
  );

  if (coverageQuery.isLoading || isLoading) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body-medium" color="textSecondary">
          Loading coverage notes...
        </Typography>
      </Box>
    );
  }

  if (coverageQuery.isError || isError) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body-medium" color="danger">
          Failed to load coverage notes. Please try again.
        </Typography>
      </Box>
    );
  }

  if (coverages.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body-medium" color="textSecondary">
          No coverages available for this RFP.
        </Typography>
      </Box>
    );
  }

  return (
    <AnchorNav
      anchorMenuItems={anchorItems}
      AnchorMenuListSxProps={{ py: 0 }}
      anchorMenuWidth={180}
    >
      {({ scrollContainerRef, setSectionRef }) => (
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {coverages.map((coverage) => {
            const noteValue = localNotes[coverage.noteKey] ?? "";
            const serverValue = serverNotes?.[coverage.noteKey] ?? "";
            const normalizedNoteValue = noteValue.trim();
            const normalizedServerValue = serverValue.trim();
            const hasChanged = normalizedNoteValue !== normalizedServerValue;
            const label = coverage.label;

            return (
              <Box
                key={coverage.noteKey}
                ref={(el: HTMLDivElement | null) => {
                  setSectionRef(el, coverage.noteKey);
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ mb: 1 }}
                >
                  {label}
                </Typography>
                <TextField
                  multiline
                  rows={6}
                  fullWidth
                  placeholder={`${label} notes`}
                  value={noteValue}
                  onChange={(e) =>
                    handleNoteChange(coverage.noteKey, e.target.value)
                  }
                  inputProps={{ maxLength: MAX_LENGTH }}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 0.5,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    {noteValue.length} / {MAX_LENGTH}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={!hasChanged || isSaving}
                      onClick={() => handleSave(coverage.noteKey)}
                    >
                      Save
                    </Button>
                  </Stack>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </AnchorNav>
  );
};

export default CoverageNoteContent;
